// RewardsTab — Phase 5 Task 13 port of docs/mockups/leaderboard-detail.html
// states starting at L1942 (default + preview), L2147 (empty), L2266 (inline
// drawer — adding), L2438 (inline drawer — editing), L2602 (Delete reward
// confirm modal).
//
// Layout: rewards table card on the left + a live "Reward preview" sidebar on
// the right that matches each current-top entry against the configured rewards
// using the same rules the backend uses when materializing grants (rank_range:
// rank ∈ [from..to]; percentile: entry's percentile rank ∈ [from..to]).
//
// Reward range fields are immutable per the current API
// (RewardUpdateParams accepts only {name?, payload?, position?}), so the
// edit-mode drawer only sends those three. Reordering via drag is OUT of scope
// for T13; the drag handle is rendered but inert. Users can change position via
// the inline editor.
//
// t-24 adds tier binding on top: on a segmentation=tier board the table grows
// a Tier column, the add-form grows a tier selector, and the preview is
// grouped per tier. Ported from .codelane/artifacts/t-24/rewards-tab-tier.html
// (state A list + orphan, B add-form, C untouched non-tier board). Nothing of
// this renders when segmentation !== "tier".
//
// t-43 adds the file half — Export JSON / Import JSON next to Add reward
// (.codelane/artifacts/t-43/rewards-tab-import-export.html state A). Import is
// a whole screen of its own; it lives in ImportRewardsModal.

import {
  Fragment,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Menu } from "../../../components/Menu/Menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Clock, Download, Plus, Upload } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Modal } from "../../../components/Modal/Modal";
import { Button } from "../../../components/Button/Button";
import { Select } from "../../../components/Select/Select";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { useToast } from "../../../components/Toast/toastContext";
import { ApiError, describeError } from "../../../lib/errors";
import {
  cancelPackage,
  cancelPackageField,
  createReward,
  deleteReward,
  dismissPackage,
  exportRewards,
  getGuildContributions,
  getPendingPackage,
  lbKeys,
  listCycleTop,
  listCycles,
  listRewards,
  schedulePackage,
  updateReward,
} from "../api";
import type {
  CreateRewardInput,
  Cycle,
  Entry,
  GuildContributionMember,
  GuildContributions,
  Leaderboard,
  RankBasis,
  Reward,
  RewardSpec,
  RewardsImportResult,
  Tier,
  UpdateRewardInput,
} from "../types";
import { isTierThresholdReward } from "../types";
import { isMemberContribution } from "../autoScored";
import {
  parseRewardPayload,
  rewardPayloadText,
  rewardPayloadToInput,
} from "../rewardPayload";
import { rewardsFilename } from "../rewardsFile";
import { AppliedStrip, PendingBanner } from "../pendingBanner";
import { buildDiff } from "../pendingDiff";
import { boardHasNextCycle, wasCreateOnly } from "../pendingFields";
import { PendingRewardsDiff } from "../pendingRewardsView";
import { SaveNowConflictModal } from "../pendingModals";
import { cutEvent, formatShortCut, nextCutOf } from "../pendingWhen";
import { rankCoverageGroups } from "../rewardCoverage";
import { diffRewardTables, rewardIdentity, samePayload } from "../rewardsDiff";
import { ImportRewardsModal } from "./ImportRewardsModal";
import pendingStyles from "../pending.module.css";
import styles from "./RewardsTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Reward kind helpers — derive the kind from Reward.rank_from/percentile_from/
// tier_key. (Reward has no explicit `kind` field; the DB enforces the three-way
// split via lb_rewards_range_xor.)
// ──────────────────────────────────────────────────────────────────────────────

type RewardKind = "rank_range" | "percentile" | "tier_threshold";

function rewardKind(
  r: Pick<Reward, "rank_from" | "percentile_from" | "tier_key">,
): RewardKind {
  // Neither range plus a tier_key is the claim_gated ladder rung — checked
  // first, because a threshold reward has no range to fall back on.
  if (isTierThresholdReward(r)) return "tier_threshold";
  if (r.percentile_from !== undefined && r.percentile_from !== null) {
    return "percentile";
  }
  return "rank_range";
}

function rewardRangeText(r: Reward): string {
  const kind = rewardKind(r);
  // A threshold reward has no range at all — the Tier column carries that
  // role instead.
  if (kind === "tier_threshold") return "—";
  if (kind === "percentile") {
    return `${r.percentile_from} – ${r.percentile_to}%`;
  }
  return `${r.rank_from} – ${r.rank_to}`;
}

/**
 * Chip-tint by reward category. The mockup (L309–311) uses three reward-chip
 * colors: default `accent-alpha`, `purple`, `amber`. We pick by kind so the
 * mapping is stable across re-renders and across previews of different
 * leaderboards:
 *   - percentile → amber (matches mockup's "Top 10%")
 *   - rank_range with rank_from === 1 → accent (matches "Champion")
 *   - rank_range otherwise → purple (matches "Top 10")
 */
function chipClassFor(r: Reward): string {
  if (rewardKind(r) === "percentile") return styles.chipAmber;
  if (r.rank_from === 1) return styles.chipAccent;
  return styles.chipPurple;
}

/** Type badge tint. tier_threshold is accent — it's the board's own mechanic
   rather than a slice of the ranking. */
function badgeClassFor(kind: RewardKind): string {
  if (kind === "tier_threshold") return styles.badgeAccent;
  if (kind === "percentile") return styles.badgeWarn;
  return styles.badgeInfo;
}

// ──────────────────────────────────────────────────────────────────────────────
// Tier binding (t-24) — Reward.tier_key against the board's tier_config.
//
// ValidateRewardCreate (internal/leaderboard/validate.go:213) rejects a
// tier_key on a non-tier board as well as one that is absent from tier_config,
// so the selector shows up only for segmentation=tier and only offers keys
// that are actually configured. A reward with no tier_key applies inside EVERY
// tier — the grant SQL matches `r.tier_key IS NULL OR r.tier_key =
// ranked.tier_key` (reward_repository.go:228).
//
// tier_key is immutable after creation (RewardUpdateParams carries only
// name/payload/position), so a tier dropped from the config strands the
// rewards bound to it: they match nothing and the only fix is delete +
// recreate. Those are surfaced as "orphans" — a banner plus a red row.
// ──────────────────────────────────────────────────────────────────────────────

/** Sentinel for "all tiers" — the option that omits tier_key entirely. */
const ALL_TIERS = "";

/** How many entries per tier the grouped preview loads. */
const TIER_PREVIEW_LIMIT = 5;

function boardTiers(lb: Leaderboard): Tier[] {
  if (lb.segmentation !== "tier") return [];
  return lb.tier_config?.tiers ?? [];
}

function tierLabel(t: Tier): string {
  return t.name.trim() || t.key;
}

/** The reward names a tier the config no longer has. */
function isOrphanTierKey(r: Reward, tiers: Tier[]): boolean {
  if (!r.tier_key) return false;
  return !tiers.some((t) => t.key === r.tier_key);
}

/** Rewards that pay out inside `tierKey` — its own, plus the unbound ones. */
function rewardsForTier(rewards: Reward[], tierKey: string): Reward[] {
  return rewards.filter((r) => !r.tier_key || r.tier_key === tierKey);
}

// ──────────────────────────────────────────────────────────────────────────────
// Preview matcher — same predicate the backend uses when grants are
// materialized on cycle-close. See internal/leaderboard/rewards.go.
//
// For a top entry of rank R out of N total entries:
//   percentile_rank = (1 - (R - 1) / N) * 100
// A reward with percentile_from=90, percentile_to=100 matches when
// percentile_rank ∈ [90, 100].
// ──────────────────────────────────────────────────────────────────────────────

function rewardMatchesRank(r: Reward, rank: number, total: number): boolean {
  // Threshold rewards are never materialized on cycle close — the grant JOIN
  // requires a rank or percentile range — so they can't appear in a preview of
  // who wins what when the cycle ends.
  if (rewardKind(r) === "tier_threshold") return false;
  if (rewardKind(r) === "rank_range") {
    if (r.rank_from === undefined || r.rank_to === undefined) return false;
    return rank >= r.rank_from && rank <= r.rank_to;
  }
  if (r.percentile_from === undefined || r.percentile_to === undefined) {
    return false;
  }
  if (total <= 0) return false;
  const percentile = (1 - (rank - 1) / total) * 100;
  return percentile >= r.percentile_from && percentile <= r.percentile_to;
}

function rewardMatchesEntry(r: Reward, entry: Entry, total: number): boolean {
  return rewardMatchesRank(r, entry.rank, total);
}

function shortCycleId(id: string | undefined): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, 12);
}

/** The room a bucket board's rows sit in, shortened the same way. */
function shortRoomId(id: string | undefined): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, 8);
}

/**
 * The most rows of one room the two new panels will load, and what they load
 * when the board does not say how big a room is (`segmentation=none`, where
 * there is no room at all and the page is simply the top of the cycle).
 *
 * The cap exists because `bucket_size` has no upper bound and this panel is a
 * 320px sidebar; it deliberately does NOT bound the arithmetic — see
 * `roomTotal`, which keeps the percentile denominator honest when the page
 * stops short of the room.
 */
const ROOM_PREVIEW_MAX = 50;
const ROOM_PREVIEW_FALLBACK = 10;

/**
 * How many rows to ask for. A room of 20 has to be loaded WHOLE: a reward on
 * 11–20 that reaches nobody in the loaded page reads as a reward that reaches
 * nobody at all, which is the opposite of what the panel is for.
 */
function roomPreviewLimit(size: number | null | undefined): number {
  if (size === null || size === undefined) return ROOM_PREVIEW_FALLBACK;
  return Math.min(size, ROOM_PREVIEW_MAX);
}

/**
 * How many teams the room HOLDS, which is the denominator a percentile band is
 * measured against — the backend divides by the entries in the partition, not
 * by whatever a screen happened to load.
 *
 * A page shorter than the limit is the whole room, so its own length is exact.
 * A page that filled the limit may have more behind it, and the room's seat
 * count is then the closest number there is; where the limit IS the seat count
 * (every room under the cap) a full page means a full room, and the two agree.
 */
function roomTotal(entries: Entry[], limit: number, size: number | null | undefined): number {
  if (entries.length < limit) return entries.length;
  return size ?? entries.length;
}

/**
 * One room, as the pair of reads behind the Room preview leaves it: which room
 * it is, and the page of it that was loaded. `bucketId` is absent on a board
 * with `segmentation=none`, where the cycle is the only ranking there is.
 */
interface RoomPreview {
  bucketId: string | undefined;
  entries: Entry[];
}

// ──────────────────────────────────────────────────────────────────────────────
// The two reward tables of a member_contribution board (rank_basis).
//
// The board has two independent prize tables and they pay out together: the
// TEAM's place in its room, granted to the team's contributors, and the
// MEMBER's place by contribution inside their own team, granted in every team
// that entered. A player can collect from both when one window closes.
//
// `rank_basis` is what tells them apart, and a reward is identified by the PAIR
// (basis + range) — the same `1 – 1` in both tables is two different rewards.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * The basis a reward is stored with. Absent means `entity`, the same
 * substitution the server makes: every reward written before the column
 * existed is one.
 */
function basisOf(r: Pick<Reward, "rank_basis">): RankBasis {
  return r.rank_basis === "guild_member" ? "guild_member" : "entity";
}

/**
 * How the basis badge reads in a table header. Deliberately NOT the column
 * value: `entity` is the name of the COLUMN, and what the operator needs to
 * know is which entity this board ranks — on scope=guild_aggregated that is
 * the guild, so the badge says `basis: guild` over a table whose rows are
 * stored as `entity`.
 */
const BASIS_BADGE: Record<RankBasis, string> = {
  entity: "basis: guild",
  guild_member: "basis: guild_member",
};

/** Does a rank-range reward cover this place? */
function rewardCoversRank(r: Reward, rank: number | undefined): boolean {
  if (rank === undefined) return false;
  if (rewardKind(r) !== "rank_range") return false;
  if (r.rank_from === undefined || r.rank_to === undefined) return false;
  return rank >= r.rank_from && rank <= r.rank_to;
}

/**
 * Chip tint in the TWO NEW panels, and only there.
 *
 * Two mockups disagree about one colour, so there are two rules. In
 * docs/mockups/leaderboard-detail.html — which `chipClassFor` above is a port
 * of, and which paints the preview of every other board on this tab — a
 * multi-place `Top 10` is purple and amber belongs to a percentile band. In
 * t-149's «rewards — team and personal» the amber marks any range WIDER than
 * one place (`Top 5` = 4–5, `Top 10` = 6–10), purple a single place below the
 * first (`Silver sail`, `Second mate`), and accent first place.
 *
 * One function cannot satisfy both, and `chipClassFor` is load-bearing for
 * boards this feature must not touch — so the t-149 reading lives here and is
 * applied by the Room and Team previews alone.
 */
function contributionChipClass(r: Reward): string {
  if (rewardKind(r) === "percentile") return styles.chipAmber;
  // A band, whichever place it starts at: more than one team (or member) takes
  // this prize, which is exactly what the mockup's amber says.
  if (
    r.rank_from !== undefined &&
    r.rank_to !== undefined &&
    r.rank_to > r.rank_from
  ) {
    return styles.chipAmber;
  }
  if (r.rank_from === 1) return styles.chipAccent;
  return styles.chipPurple;
}

/** Why a member row carries no rank, in the words the preview prints. */
function noRankNote(m: GuildContributionMember): string | null {
  if (m.rank !== undefined) return null;
  return m.participating ? " · no contribution" : " · not participating";
}

function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

/**
 * The import toast's line — the server's own counters, not the plan's. The
 * plan predicted what would happen; these are what did, and a board that moved
 * between the two is exactly the case worth reading. Everything at zero says
 * so in words rather than as four zeroes.
 */
function importedSummary(r: RewardsImportResult): string {
  const parts = [
    r.created > 0 ? `${r.created} created` : "",
    r.updated > 0 ? `${r.updated} updated` : "",
    r.deleted > 0 ? `${r.deleted} deleted` : "",
    r.unchanged > 0 ? `${r.unchanged} unchanged` : "",
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Nothing changed";
}

// ──────────────────────────────────────────────────────────────────────────────
// RewardsTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────────
// The prize table as one edit (t-159)
//
// The table is edited as a WHOLE here, because the unit of a scheduled change
// is the whole table: a package carries the replacement, never a list of edits
// to the rows on the board. A reward has no key that outlives an edit — the id
// belongs to the row the server is holding — so "row 3 becomes this" is not
// something a package can say, and "the table becomes this" is.
//
// So the drawer stages into a draft and the save bar is where the operator
// chooses what happens to it: applied now, row by row, exactly as this tab has
// always applied them, or handed to the next cut in one piece.
// ──────────────────────────────────────────────────────────────────────────────

/** A row that exists only in the draft. The server never issues an id in this
   shape, so no stored reward can be mistaken for one. */
const DRAFT_ID = "draft:";

let draftSeq = 0;

function draftRewardId(): string {
  draftSeq += 1;
  return `${DRAFT_ID}${draftSeq}`;
}

function isDraftRow(r: Reward): boolean {
  return r.id.startsWith(DRAFT_ID);
}

/** Has the operator moved the table away from what the server holds? */
function tableChanged(before: Reward[], after: Reward[]): boolean {
  if (before.length !== after.length) return true;
  return after.some((row, i) => {
    const was = before[i];
    return (
      !was ||
      was.id !== row.id ||
      was.name !== row.name ||
      was.position !== row.position ||
      !samePayload(was.payload, row.payload)
    );
  });
}

/**
 * A draft row as the POST body this tab has always sent. Every optional key is
 * omitted rather than sent empty: `rank_basis` absent IS `entity` on the
 * server, and a null `tier_key` is what makes a reward match inside every
 * tier — so a body that names them would not be the same request.
 */
function createInputOf(row: Reward): CreateRewardInput {
  const input: CreateRewardInput = {
    name: row.name,
    position: row.position,
    payload: row.payload,
  };
  if (row.rank_basis === "guild_member") input.rank_basis = "guild_member";
  if (row.tier_key) input.tier_key = row.tier_key;
  if (row.rank_from !== undefined) {
    input.rank_from = row.rank_from;
    input.rank_to = row.rank_to;
  } else if (row.percentile_from !== undefined) {
    input.percentile_from = row.percentile_from;
    input.percentile_to = row.percentile_to;
  }
  return input;
}

/** A draft row as the package carries it: no id, no timestamps — those belong
   to a row on the board, and a scheduled table has none yet. */
function rewardSpecOf(row: Reward): RewardSpec {
  return {
    tier_key: row.tier_key,
    rank_from: row.rank_from,
    rank_to: row.rank_to,
    percentile_from: row.percentile_from,
    percentile_to: row.percentile_to,
    rank_basis: row.rank_basis,
    name: row.name,
    payload: row.payload,
    position: row.position,
  };
}

/**
 * A scheduled table put back into the editor, matched onto the rows the board
 * holds by range. A row the board already has keeps its id, so saving the
 * draft afterwards patches it instead of dropping and re-creating a reward
 * that never actually changed.
 */
function rowsFromSpecs(
  specs: RewardSpec[],
  onBoard: Reward[],
  lbId: string,
): Reward[] {
  const byIdentity = new Map(onBoard.map((r) => [rewardIdentity(r), r]));
  return specs.map((spec) => {
    const existing = byIdentity.get(rewardIdentity(spec));
    if (existing) {
      return { ...existing, name: spec.name, payload: spec.payload, position: spec.position };
    }
    return {
      id: draftRewardId(),
      leaderboard_id: lbId,
      rank_basis: spec.rank_basis ?? "entity",
      tier_key: spec.tier_key,
      rank_from: spec.rank_from,
      rank_to: spec.rank_to,
      percentile_from: spec.percentile_from,
      percentile_to: spec.percentile_to,
      name: spec.name,
      payload: spec.payload,
      position: spec.position,
      created_at: "",
    };
  });
}

/**
 * The draft, applied to the board the way this tab has always applied a row:
 * one request per row that actually moved. Sequential rather than parallel —
 * positions are unique per board, so two writes racing over the same one is a
 * conflict the server would be right to refuse.
 *
 * Ranges are not patched, because `RewardUpdateParams` does not take them:
 * the editor freezes them on an existing row, so a row that survives an edit
 * only ever moved its name, its position or its payload.
 */
async function applyRewardTable(
  gameId: string,
  lbId: string,
  before: Reward[],
  after: Reward[],
): Promise<void> {
  const kept = new Set(after.map((r) => r.id));
  for (const row of before) {
    if (!kept.has(row.id)) await deleteReward(gameId, lbId, row.id);
  }
  const previous = new Map(before.map((r) => [r.id, r]));
  for (const row of after) {
    const was = previous.get(row.id);
    if (!was) {
      await createReward(gameId, lbId, createInputOf(row));
      continue;
    }
    if (
      was.name === row.name &&
      was.position === row.position &&
      samePayload(was.payload, row.payload)
    ) {
      continue;
    }
    const input: UpdateRewardInput = {
      name: row.name,
      position: row.position,
      payload: row.payload,
    };
    await updateReward(gameId, lbId, row.id, input);
  }
}

/**
 * An immediate save of the table, and whether it also takes the scheduled
 * replacement down with it. The pair travels together because both halves are
 * one answer to one question the operator was asked.
 */
interface ImmediateTableSave {
  rows: Reward[];
  dropScheduled: boolean;
}

interface Props {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
}

export function RewardsTab({ gameId, lbId, lb }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();
  const tiers = useMemo(() => boardTiers(lb), [lb]);
  const isTierBoard = lb.segmentation === "tier";
  // Threshold rewards only exist on a claim_gated board; everywhere else the
  // editor stays exactly as it was.
  const isGatedBoard = isTierBoard && lb.tier_mode === "claim_gated";
  // The board with two reward tables. Everything below that is not gated on
  // this renders exactly as it did before the second table existed.
  const twoTables = isMemberContribution(lb);

  const rewardsQ = useQuery({
    queryKey: lbKeys.rewards(gameId, lbId),
    queryFn: () => listRewards(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  // Cycles → pick the currently-open one (or fall back to the first) so the
  // preview shows live grant projection against the active top.
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });
  const previewCycle: Cycle | undefined = useMemo(() => {
    const cs = cyclesQ.data?.cycles ?? [];
    return cs.find((c) => c.status === "open") ?? cs[0];
  }, [cyclesQ.data]);

  const topQ = useQuery({
    queryKey: lbKeys.top(gameId, lbId, previewCycle?.id ?? "", { limit: 10 }),
    queryFn: () => {
      if (!previewCycle) return Promise.reject(new Error("no cycle"));
      return listCycleTop(gameId, lbId, previewCycle.id, { limit: 10 });
    },
    // A tier board previews per tier, and a two-table board previews one ROOM
    // (see roomQ) — neither has any use for the cycle-wide top.
    enabled: !!previewCycle && !isTierBoard && !twoTables,
  });

  // === The room preview (member_contribution) ======================== //
  // Two reads, because the panel is about ONE room and nothing on the board
  // says which one: the first finds the room the cycle's leading team sits in,
  // the second loads that room's own page. Ranks inside it are the ranks the
  // team rewards are handed out by (MaterializeGrants partitions by
  // bucket_id), so a cycle-wide top would offer first place to the leader of
  // the cycle rather than to the leader of each room.
  //
  // A board with `segmentation=none` has no room to scope to; there the first
  // read IS the ranking, and the second is not made.
  const roomLimit = roomPreviewLimit(lb.bucket_size);
  const roomQ = useQuery({
    queryKey: lbKeys.roomPreview(gameId, lbId, previewCycle?.id ?? "", roomLimit),
    queryFn: async (): Promise<RoomPreview> => {
      if (!previewCycle) return Promise.reject(new Error("no cycle"));
      const cycleId = previewCycle.id;
      const lead = await listCycleTop(gameId, lbId, cycleId, {
        limit: roomLimit,
      });
      const bucketId = lead.entries?.[0]?.bucket_id;
      if (!bucketId) return { bucketId: undefined, entries: lead.entries ?? [] };
      const room = await listCycleTop(gameId, lbId, cycleId, {
        limit: roomLimit,
        bucket_id: bucketId,
      });
      return { bucketId, entries: room.entries ?? [] };
    },
    enabled: twoTables && !!previewCycle,
  });

  // On a tier board the ranking restarts inside every tier (MaterializeGrants
  // partitions by tier_key), so one flat top would mis-state who wins what.
  // Each tier gets its own top instead, and the preview is grouped to match.
  //
  // Top league first: tier_config runs bottom-up in both modes (score_bucket
  // is sorted by ascending min_score, promotion's array order is the ladder
  // from the lowest rung), and the preview leads with the strongest tier.
  const previewTiers = useMemo(() => [...tiers].reverse(), [tiers]);
  const tierKeys = useMemo(
    () => previewTiers.map((t) => t.key),
    [previewTiers],
  );
  const tierTopQ = useQuery({
    queryKey: lbKeys.topByTier(
      gameId,
      lbId,
      previewCycle?.id ?? "",
      tierKeys,
      TIER_PREVIEW_LIMIT,
    ),
    queryFn: async () => {
      if (!previewCycle) return Promise.reject(new Error("no cycle"));
      const cycleId = previewCycle.id;
      return Promise.all(
        previewTiers.map(async (tier) => {
          const res = await listCycleTop(gameId, lbId, cycleId, {
            limit: TIER_PREVIEW_LIMIT,
            tier_key: tier.key,
          });
          return { tier, entries: res.entries ?? [] };
        }),
      );
    },
    enabled: !!previewCycle && isTierBoard && previewTiers.length > 0,
  });

  // Editor state: { kind: "add" } for the inline add-drawer; { kind: "edit",
  // id } when editing a row in-place. `basis` says WHICH table the drawer
  // belongs to — one editor at a time across both, and the row opens inside
  // the card that was pressed. An edit carries the reward's own basis.
  const [editor, setEditor] = useState<
    | { kind: "add"; basis: RankBasis }
    | { kind: "edit"; reward: Reward }
    | null
  >(null);
  const editorBasis: RankBasis | null =
    editor === null
      ? null
      : editor.kind === "add"
        ? editor.basis
        : basisOf(editor.reward);

  // Delete confirm modal — separate from the inline editor.
  const [deleteTarget, setDeleteTarget] = useState<Reward | null>(null);

  // === The reward table as a file (t-43) ============================== //
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // One closure for as long as the tab lives. Modal is indifferent either way
  // — it reaches onClose through a ref and keys its focus effect on `open`
  // alone — so this is tidiness, not a constraint the dialog imposes.
  const closeImport = useCallback(() => setImportOpen(false), []);

  async function onExport() {
    setExporting(true);
    try {
      // Success is silent: the browser has the file, and a toast about it
      // would land on a screen that never changed.
      await exportRewards(gameId, lbId, rewardsFilename(lb.ext_key));
    } catch (err) {
      push({
        variant: "danger",
        title: "Couldn't export the rewards",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    } finally {
      setExporting(false);
    }
  }

  function onImported(result: RewardsImportResult) {
    setImportOpen(false);
    // The response carries the table the transaction left behind, which is
    // exactly what a re-fetch would go and ask for — so it is put in the cache
    // rather than thrown away and requested again.
    qc.setQueryData(lbKeys.rewards(gameId, lbId), result.rewards);
    // The file replaced the table whole. A draft laid over it would be an edit
    // to a table that no longer exists, so it goes with it.
    setDraft(null);
    push({
      variant: "success",
      title: "Rewards imported",
      message: importedSummary(result),
    });
  }

  // Memoised for its identity, not its cost: while the query has no data the
  // `?? []` fallback is a fresh array every render, and the sort below depends
  // on this list.
  const rewards = useMemo(() => rewardsQ.data ?? [], [rewardsQ.data]);
  const sortedRewards = useMemo(
    () => [...rewards].sort((a, b) => a.position - b.position),
    [rewards],
  );

  // === The table as an edit (t-159) ================================== //
  // `null` while the tab shows exactly what the server holds — which is what
  // it shows until the operator commits something in the drawer.
  const [draft, setDraft] = useState<Reward[] | null>(null);
  /** What the cards, the previews and both saves are about. */
  const tableRows = draft ?? sortedRewards;
  const dirty = draft !== null && tableChanged(sortedRewards, draft);
  /** Which rows carry the unsaved mark. A row the server never saw is one;
     so is one whose name, position or payload has moved. A row that was
     DELETED has no mark because it has no row left to put one on — the count
     in the bar is what says so. */
  const editedIds = useMemo(() => {
    if (draft === null) return new Set<string>();
    const before = new Map(sortedRewards.map((r) => [r.id, r]));
    return new Set(
      draft
        .filter((row) => {
          if (isDraftRow(row)) return true;
          const was = before.get(row.id);
          return (
            !was ||
            was.name !== row.name ||
            was.position !== row.position ||
            !samePayload(was.payload, row.payload)
          );
        })
        .map((r) => r.id),
    );
  }, [draft, sortedRewards]);
  const topEntries = useMemo(() => topQ.data?.entries ?? [], [topQ.data]);
  // The room's own page: the rows the two new panels rank, count and match
  // rewards against. One list for both, so they cannot disagree — and one
  // denominator with it, which is the room's size rather than the page's when
  // the two differ.
  const roomEntries = useMemo(() => roomQ.data?.entries ?? [], [roomQ.data]);
  const roomSize = roomTotal(roomEntries, roomLimit, lb.bucket_size);
  const orphans = useMemo(
    () => (isTierBoard ? tableRows.filter((r) => isOrphanTierKey(r, tiers)) : []),
    [isTierBoard, tableRows, tiers],
  );

  // The two tables, split once. On every other board the second list is empty
  // and the first one IS the whole table — the tab renders one card from it,
  // exactly as it always has.
  const entityRewards = useMemo(
    () => tableRows.filter((r) => basisOf(r) === "entity"),
    [tableRows],
  );
  const memberRewards = useMemo(
    () => tableRows.filter((r) => basisOf(r) === "guild_member"),
    [tableRows],
  );

  // === The package this board is holding (t-159) ===================== //
  // One package per board, and it carries both halves: the board's own fields
  // and the prize table. The banner is the same one the Settings tab draws —
  // it just expands into this half of the diff.

  const nextCut = nextCutOf(lb.window_kind, cyclesQ.data?.cycles?.[0]);
  /** Whether this board ever cuts another cycle. On one that does not there is
     no moment a package could be applied at, so the second save is not
     offered at all. */
  const boardCuts = boardHasNextCycle(lb.window_kind);

  // Its own request: GET /leaderboards/{id} does not carry the package, and
  // the great majority of boards have none — a 404 answered as `null`.
  const pendingQ = useQuery({
    queryKey: lbKeys.pending(gameId, lbId),
    queryFn: () => getPendingPackage(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });
  const pkg = pendingQ.data ?? null;
  /** Only a package still waiting is a promise. An applied one is on the board
     already; a refused one never will be. */
  const waiting = pkg?.status === "pending" ? pkg : null;
  /**
   * The package could not be read — not the same answer as "there is none".
   * With `pkg` null there is no banner and no collision dialog, so an
   * immediate save goes out blind and a scheduled table nobody saw overwrites
   * it at the next cut. Only where a package could exist: a board that cuts no
   * further cycle holds none, and has no second save to fall back on.
   */
  const packageUnknown = pendingQ.isError && boardCuts;
  const [expanded, setExpanded] = useState(false);
  /** The rows an immediate save is holding while the operator answers for the
     scheduled table it would cancel. */
  const [conflictRows, setConflictRows] = useState<Reward[] | null>(null);

  const invalidateRewards = useCallback(() => {
    void qc.invalidateQueries({ queryKey: lbKeys.rewards(gameId, lbId) });
  }, [qc, gameId, lbId]);
  const invalidatePending = useCallback(() => {
    void qc.invalidateQueries({ queryKey: lbKeys.pending(gameId, lbId) });
  }, [qc, gameId, lbId]);
  const pushFailure = useCallback(
    (title: string, err: unknown) => {
      push({
        variant: "danger",
        title,
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
    [push],
  );

  const saveNowMut = useMutation({
    mutationFn: async ({ rows, dropScheduled }: ImmediateTableSave) => {
      await applyRewardTable(gameId, lbId, sortedRewards, rows);
      if (!dropScheduled) return { undropped: false };
      // Setting the table by hand is the last word on it, so the table the
      // package was holding goes with the save — §5's rule, which until now
      // this tab did not obey at all. Reported rather than thrown: the rows
      // are written by the time this runs, and a failure banner over a table
      // that HAS changed invites the operator to write them all again.
      try {
        await cancelPackageField(gameId, lbId, "rewards");
        return { undropped: false };
      } catch {
        return { undropped: true };
      }
    },
    onSuccess: ({ undropped }) => {
      setDraft(null);
      setConflictRows(null);
      invalidateRewards();
      invalidatePending();
      if (undropped) {
        push({
          variant: "warn",
          title: "Saved, with the scheduled table left over",
          message:
            "The prize table is saved, but the scheduled replacement could not be taken out of the package — it still applies at the next cut and would overwrite what you just saved.",
        });
      }
    },
    onError: (err) => {
      // Not one request but a series of them, and a refusal in the middle
      // leaves half the rows written. So the tab stops pretending it knows
      // what the board holds: the list is asked for again, and the draft — an
      // edit computed against a list that has just been proved stale — goes
      // with it. Kept, it would make the next "Save now" delete rows already
      // deleted and create rows already created.
      setDraft(null);
      setConflictRows(null);
      invalidateRewards();
      push({
        variant: "danger",
        title: "Couldn't save the prize table",
        message: `${
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal")
        } Part of the table may have been written; the tab has been reloaded to show what the board actually holds.`,
      });
    },
  });

  const scheduleMut = useMutation({
    // `fields: {}` on purpose: this tab schedules the prize table and nothing
    // else, and the package merges by key — an empty object leaves whatever
    // the Settings tab put there alone.
    mutationFn: (rows: Reward[]) =>
      schedulePackage(gameId, lbId, {
        fields: {},
        rewards: rows.map(rewardSpecOf),
      }),
    onSuccess: () => {
      setDraft(null);
      invalidatePending();
      push({
        variant: "success",
        title: "Scheduled for the next cycle",
        message:
          `The board keeps paying out by the table it has until ${cutEvent(nextCut)}.`,
      });
    },
    onError: (err) => pushFailure("Couldn't schedule the prize table", err),
  });

  const cancelAllMut = useMutation({
    mutationFn: () => cancelPackage(gameId, lbId),
    onSuccess: () => {
      setExpanded(false);
      invalidatePending();
    },
    onError: (err) => pushFailure("Couldn't cancel the scheduled changes", err),
  });

  const cancelFieldMut = useMutation({
    // The server takes `rewards` as a field name of its own: the prize table
    // is one entry of the package, however many rows it replaces. Every other
    // name is an ordinary column, and this tab draws those only on a package
    // that carries no table at all.
    mutationFn: (field: string) => cancelPackageField(gameId, lbId, field),
    onSuccess: (_data, field) => {
      invalidatePending();
      if (field === "rewards") {
        push({
          variant: "success",
          message: "Prize table removed from the package",
        });
      }
    },
    onError: (err) => pushFailure("Couldn't cancel the scheduled change", err),
  });

  const dismissMut = useMutation({
    mutationFn: () => dismissPackage(gameId, lbId),
    onSuccess: invalidatePending,
    onError: (err) => pushFailure("Couldn't dismiss the package", err),
  });

  const packageBusy =
    cancelAllMut.isPending || cancelFieldMut.isPending || dismissMut.isPending;

  // --- The package as this tab reads it -------------------------------------

  /** The whole package as diff rows. `rewards` is folded in as a field so the
     banner counts the prize table as the one change it is — the server keeps
     it in a column of its own, and nothing else about it is different. */
  const pkgGroups = useMemo(() => {
    if (!pkg) return [];
    const fields = pkg.rewards
      ? { ...pkg.fields, rewards: pkg.rewards }
      : pkg.fields;
    return buildDiff(fields, lb, {
      blameField: pkg.failure_field,
      currentRewards: sortedRewards,
      // An applied package's values are on the board — that is what applying
      // it meant — so a diff that drops matching rows drops all of them, and
      // the strip above announces "0 scheduled changes applied". A refused
      // package is a record too, of what was turned down.
      keepUnchanged: pkg.status !== "pending",
    });
  }, [pkg, lb, sortedRewards]);
  /** Everything the Settings tab is about — counted here, drawn there. */
  const boardRows = useMemo(
    () =>
      pkgGroups
        .filter((g) => g.section !== "Prize table")
        .flatMap((g) => g.rows),
    [pkgGroups],
  );
  const reopenedCount = useMemo(
    () => boardRows.filter((r) => wasCreateOnly(r.field)).length,
    [boardRows],
  );
  /** The scheduled table read against the one on the board, row by row. */
  const scheduledTable = useMemo(
    () =>
      pkg?.rewards
        ? diffRewardTables(sortedRewards.map(rewardSpecOf), pkg.rewards)
        : null,
    [pkg, sortedRewards],
  );
  /** What an immediate save would cancel: the scheduled table, read against
     the one on the board — the same row the banner draws, in the dialog that
     asks whether to take it out. */
  const conflictGroups = useMemo(
    () =>
      conflictRows && waiting?.rewards
        ? buildDiff({ rewards: waiting.rewards }, lb, {
            currentRewards: sortedRewards,
          })
        : [],
    [conflictRows, waiting, lb, sortedRewards],
  );
  /** One strip per ranking the replacement describes. The board's own tier
     keys go in as well: a table whose only tier'd rows are for `gold` still
     leaves the other leagues to be judged, and a league paying nothing is the
     hole this warning exists for. */
  const scheduledCoverage = useMemo(
    () => rankCoverageGroups(pkg?.rewards ?? [], tierKeys),
    [pkg, tierKeys],
  );

  /**
   * A row the drawer finished with. It lands in the draft rather than on the
   * board: the save bar below is where it is decided whether the table takes
   * effect now or at the next cut, and that question is about the table, not
   * about the row.
   */
  const commitRow = useCallback(
    (row: Reward) => {
      setDraft((current) => {
        const base = current ?? sortedRewards;
        const at = base.findIndex((r) => r.id === row.id);
        const next =
          at === -1 ? [...base, row] : base.map((r) => (r.id === row.id ? row : r));
        return [...next].sort((a, b) => a.position - b.position);
      });
      setEditor(null);
    },
    [sortedRewards],
  );

  const removeRow = useCallback(
    (row: Reward) => {
      setDraft((current) =>
        (current ?? sortedRewards).filter((r) => r.id !== row.id),
      );
      setDeleteTarget(null);
    },
    [sortedRewards],
  );

  // === The team preview (member_contribution) ======================== //
  // The contributor table cannot be previewed against `/top`: that ranks the
  // TEAMS, and this table ranks inside one of them. So one team of the room
  // above is opened — and deliberately NOT its leader.
  //
  // The whole point of the second table is that the personal prizes are played
  // for inside EVERY team that entered, however the team itself placed. A
  // panel pinned to the room's first row would only ever show the one case
  // where that makes no visible difference: the team taking the top placement
  // grant as well. The room's LAST loaded row shows both grants at once — the
  // members' own places, and whatever band the team's own place falls in — so
  // the last row is the one that answers the question the table exists for.
  //
  // A room holding a single team has no other row to pick, and there the
  // leader is also the last.
  //
  // Which team that is moves as the standings do — the panel says «live
  // preview» and every row of it is a place that can change, so a refresh
  // naming a different team is the panel working, not a fault.
  const previewGuild = twoTables ? roomEntries[roomEntries.length - 1] : undefined;
  const previewGuildId = previewGuild?.player.id;
  const contributionsQ = useQuery({
    queryKey: lbKeys.guildContributions(
      gameId,
      lbId,
      previewCycle?.id ?? "",
      previewGuildId ?? "",
    ),
    queryFn: () => {
      if (!previewCycle || !previewGuildId) {
        return Promise.reject(new Error("no team to preview"));
      }
      return getGuildContributions(gameId, lbId, previewCycle.id, previewGuildId);
    },
    enabled: twoTables && !!previewCycle && !!previewGuildId,
  });

  // === Loading / error =============================================== //
  if (rewardsQ.isError) {
    return (
      <Alert variant="danger" title="Couldn't load rewards">
        {describeQueryError(rewardsQ.error)}
      </Alert>
    );
  }

  if (rewardsQ.isLoading) {
    return (
      <div className={styles.layout}>
        <div className={styles.card}>
          <Skeleton height={260} />
        </div>
        <aside className={styles.preview}>
          <Skeleton height={260} />
        </aside>
      </div>
    );
  }

  /**
   * Export and Import are BOARD-level actions — one file for the whole reward
   * table, both bases in it — so they cannot belong to one of the two cards.
   * The mockup does not draw them at all (it is about the two tables), and
   * dropping them would take the file half of the tab away from exactly the
   * board that needs it most. They stay where they have always been: the first
   * card's header, ahead of the hairline, in the order Export · Import.
   */
  const fileActions = (
    <>
      <Button
        variant="secondary"
        size="sm"
        loading={exporting}
        onClick={onExport}
        icon={<Download size={13} aria-hidden="true" />}
      >
        Export JSON
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setImportOpen(true)}
        icon={<Upload size={13} aria-hidden="true" />}
      >
        Import JSON
      </Button>
      <span className={styles.headSep} aria-hidden="true" />
    </>
  );

  /** One table card, whichever basis it is drawn for. */
  function tableCard(props: {
    title: string;
    sub?: string;
    basis: RankBasis;
    rows: Reward[];
    badge?: "neutral" | "warn";
    fileActions?: ReactNode;
    orphanCount?: number;
    /** Says what a scheduled save carries. Once per tab, under the first
       card: it is about the prize table, and a board has one of those
       however many cards it is drawn in. */
    helper?: boolean;
  }) {
    const open = editorBasis === props.basis ? editor : null;
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h3>{props.title}</h3>
            {props.sub && <div className={styles.cardSub}>{props.sub}</div>}
          </div>
          {/* Reading first, then writing: Export · Import · Add reward. */}
          <div className={styles.headActions}>
            {props.fileActions}
            {props.badge && (
              <span
                className={`${styles.basisBadge} ${
                  props.badge === "warn"
                    ? styles.basisBadgeWarn
                    : styles.basisBadgeNeutral
                }`}
              >
                {BASIS_BADGE[props.basis]}
              </span>
            )}
            <Button
              variant="primary"
              size="sm"
              /* One editor at a time across both tables: two open drawers
                 would be two half-typed rewards and one Save. */
              disabled={editor !== null}
              onClick={() => setEditor({ kind: "add", basis: props.basis })}
              icon={<Plus size={12} aria-hidden="true" />}
            >
              Add reward
            </Button>
          </div>
        </div>

        {(props.orphanCount ?? 0) > 0 && (
          <div className={styles.alertSlot}>
            <OrphanRewardsAlert count={props.orphanCount ?? 0} />
          </div>
        )}

        {open?.kind === "add" && (
          <RewardEditorRow
            mode="add"
            lbId={lbId}
            tiers={tiers}
            showTier={isTierBoard}
            gated={isGatedBoard}
            basis={props.basis}
            initial={{
              position: nextPosition(props.rows),
              name: "",
              kind: "rank_range",
              tierKey: ALL_TIERS,
              rangeFrom: "",
              rangeTo: "",
              payload: "",
            }}
            onCommit={commitRow}
            onClose={() => setEditor(null)}
          />
        )}
        {open?.kind === "edit" && (
          <RewardEditorRow
            mode="edit"
            lbId={lbId}
            tiers={tiers}
            showTier={isTierBoard}
            gated={isGatedBoard}
            basis={props.basis}
            reward={open.reward}
            initial={rewardToFormState(open.reward)}
            onCommit={commitRow}
            onClose={() => setEditor(null)}
          />
        )}

        {props.rows.length === 0 && open === null ? (
          <RewardsEmptyState
            onAdd={() => setEditor({ kind: "add", basis: props.basis })}
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 56 }} className={styles.num}>
                  Pos.
                </th>
                <th style={{ width: isTierBoard ? 150 : 130 }}>Name</th>
                {isTierBoard && <th style={{ width: 120 }}>Tier</th>}
                <th style={{ width: isTierBoard ? 104 : 110 }}>Type</th>
                <th style={{ width: isTierBoard ? 104 : 100 }}>Range</th>
                <th>Payload</th>
                <th className={styles.act} aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {props.rows
                .filter((r) => open?.kind !== "edit" || open.reward.id !== r.id)
                .map((r) => (
                  <RewardRow
                    key={r.id}
                    reward={r}
                    tiers={tiers}
                    showTier={isTierBoard}
                    edited={editedIds.has(r.id)}
                    onEdit={() => setEditor({ kind: "edit", reward: r })}
                    onDelete={() => setDeleteTarget(r)}
                  />
                ))}
            </tbody>
          </table>
        )}

        {props.helper && boardCuts && (
          <div className={styles.tableHelper}>
            The table is edited as a whole: a scheduled save carries the full
            replacement, never one row of it. A cycle pays out by the table as
            it stood the moment that cycle stopped being open — a row added
            today does not reach the week that has already closed.
          </div>
        )}
      </div>
    );
  }

  const saving = saveNowMut.isPending || scheduleMut.isPending;
  const cutLabel = formatShortCut(nextCut?.at);

  /**
   * Saving the table by hand while a replacement is scheduled. §5's rule is
   * about the package as a whole, and §8 puts the prize table in that same
   * package: setting it by hand is the operator's last word on it, so the
   * scheduled replacement has to go — and, exactly as on the Settings tab, it
   * goes only after the operator has been told it will.
   */
  function onSaveNow(rows: Reward[]) {
    if (waiting?.rewards) {
      setConflictRows(rows);
      return;
    }
    saveNowMut.mutate({ rows, dropScheduled: false });
  }

  return (
    <>
      {/* Not "there is no package" — nobody knows. The banner below is missing
          while this is up, so an immediate save would be made without knowing
          that a replacement is waiting to undo it at the next cut. */}
      {packageUnknown && (
        <Alert variant="danger" title="Couldn't load the scheduled changes">
          This board may be holding a prize table waiting for the next cut, and
          this page could not read it. Saving now would go out without knowing
          what it collides with, and whatever is waiting would overwrite it at
          the cut. Reload the page to ask again — scheduling still works,
          because a scheduled table joins the package instead of racing it.
        </Alert>
      )}

      {/* The package, in whichever of its three states it is in. Only one is
          ever on the page: the row is unique per board. And it is the same
          banner the Settings tab draws — one package, expanded here into the
          half of it this tab is about. */}
      {pkg?.status === "applied" && (
        <AppliedStrip
          pkg={pkg}
          count={pkgGroups.reduce((n, g) => n + g.rows.length, 0)}
          auditHref="?tab=audit"
          onDismiss={() => dismissMut.mutate()}
          busy={packageBusy}
        />
      )}
      {(waiting || pkg?.status === "failed") && pkg && (
        <PendingBanner
          pkg={pkg}
          groups={pkgGroups}
          nextCut={nextCut}
          expanded={expanded}
          onToggleExpanded={() => setExpanded((v) => !v)}
          onCancelAll={() => cancelAllMut.mutate()}
          onCancelField={(field) => cancelFieldMut.mutate(field)}
          busy={packageBusy}
          onDismiss={() => dismissMut.mutate()}
          onReschedule={() => {
            // The refused table goes back into the editor as an unsaved edit,
            // and the package that held it is gone: what refused was judged
            // against the board as it stands, and re-offering it unedited
            // would be refused again.
            if (pkg.rewards) {
              setDraft(rowsFromSpecs(pkg.rewards, sortedRewards, lbId));
            }
            dismissMut.mutate();
          }}
          diff={
            scheduledTable ? (
              <PendingRewardsDiff
                boardRows={boardRows}
                reopened={reopenedCount}
                settingsHref="?tab=settings"
                table={scheduledTable}
                coverage={scheduledCoverage}
                onCancel={
                  waiting ? () => cancelFieldMut.mutate("rewards") : undefined
                }
                cancelBusy={packageBusy}
              />
            ) : undefined
          }
        />
      )}

      <div className={`${styles.layout} ${dirty ? styles.layoutWithBar : ""}`}>
      {twoTables ? (
        <div className={styles.stack}>
          {tableCard({
            title: "Team placement",
            sub: "The team's place in its room. Every contributor of the team gets the grant.",
            basis: "entity",
            rows: entityRewards,
            badge: "neutral",
            fileActions,
            helper: true,
          })}
          {tableCard({
            title: "Top contributors",
            sub: "The member's place by contribution inside their own team — in every team that entered.",
            basis: "guild_member",
            rows: memberRewards,
            badge: "warn",
          })}
          <ContributorsOnlyNote
            on={lb.reward_contributors_only !== false}
          />
        </div>
      ) : (
        tableCard({
          title: "Rewards",
          basis: "entity",
          rows: tableRows,
          fileActions,
          orphanCount: orphans.length,
          helper: true,
        })
      )}

      {twoTables ? (
        <div className={styles.stack}>
          {/* Each panel answers to its OWN table's editor, by the same split
             the cards use. A draft in the contributor table changes nothing
             about the room's standings, and vice versa — one shared editor
             state would blank the panel that has nothing to wait for. */}
          <RoomPreviewSidebar
            editorState={editorBasis === "entity" ? editor : null}
            rewards={entityRewards}
            roomEntries={roomEntries}
            roomSize={roomSize}
            bucketId={roomQ.data?.bucketId}
            hasRooms={lb.segmentation === "bucket"}
            cycle={previewCycle}
            roomLoading={roomQ.isLoading}
            roomError={roomQ.isError}
          />
          <TeamPreviewSidebar
            editorState={editorBasis === "guild_member" ? editor : null}
            memberRewards={memberRewards}
            entityRewards={entityRewards}
            roomSize={roomSize}
            teamEntry={previewGuild}
            contributions={contributionsQ.data}
            rewardContributorsOnly={lb.reward_contributors_only !== false}
            loading={contributionsQ.isLoading || roomQ.isLoading}
            error={contributionsQ.isError || roomQ.isError}
          />
        </div>
      ) : (
        <RewardPreviewSidebar
          editorState={editor}
          rewards={tableRows}
          topEntries={topEntries}
          tierGroups={isTierBoard ? tierTopQ.data ?? [] : null}
          cycle={previewCycle}
          topLoading={isTierBoard ? tierTopQ.isLoading : topQ.isLoading}
          topError={isTierBoard ? tierTopQ.isError : topQ.isError}
        />
      )}

      {deleteTarget && (
        <DeleteRewardModal
          reward={deleteTarget}
          onConfirm={() => removeRow(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {importOpen && (
        <ImportRewardsModal
          gameId={gameId}
          lbId={lbId}
          lb={lb}
          onClose={closeImport}
          onImported={onImported}
        />
      )}
      </div>

      {/* The table is edited as a whole, so the choice is made about the whole
          table — the same bar, and the same two saves, the Settings tab
          offers. It appears only once the table has actually moved. */}
      {dirty && (
        <div className={styles.actionBar}>
          <div className={styles.actionBarInner}>
            <span className={`${pendingStyles.abNote} ${pendingStyles.abWarn}`}>
              <span className={pendingStyles.dot} />
              Prize table edited
              {cutLabel && (
                <span className={pendingStyles.diffNote}>
                  {" · "}
                  {nextCut?.kind === "opens" ? "next cycle opens" : "next cut"}{" "}
                  {cutLabel}
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              onClick={() => setDraft(null)}
              disabled={saving}
            >
              Discard
            </Button>
            {boardCuts && (
              <Button
                variant="secondary"
                icon={<Clock size={16} aria-hidden="true" />}
                loading={scheduleMut.isPending}
                disabled={saveNowMut.isPending}
                onClick={() => scheduleMut.mutate(tableRows)}
              >
                Schedule for next cycle
              </Button>
            )}
            {packageUnknown ? (
              /* aria-disabled rather than `disabled`, the way the config
                 form's own refused "Save now" is written: the button has
                 something to say and a disabled one is not focusable, so
                 nobody reading the page with a keyboard would hear it. */
              <Button
                variant="primary"
                className={styles.btnRefused}
                aria-disabled
                title="This board's scheduled changes couldn't be read — reload the page, or schedule this table instead"
                onClick={() => undefined}
              >
                Save now
              </Button>
            ) : (
              <Button
                variant="primary"
                loading={saveNowMut.isPending}
                disabled={scheduleMut.isPending}
                onClick={() => onSaveNow(tableRows)}
              >
                Save now
              </Button>
            )}
          </div>
        </div>
      )}

      {conflictRows && waiting?.rewards && (
        <SaveNowConflictModal
          groups={conflictGroups}
          /* The fields the Settings tab put in the same package. They are not
             what this save is about, so they stay — and the dialog says so
             rather than leaving the operator to guess. */
          remaining={Object.keys(waiting.fields).length}
          nextCut={nextCut}
          saving={saveNowMut.isPending}
          onConfirm={() =>
            saveNowMut.mutate({ rows: conflictRows, dropScheduled: true })
          }
          onClose={() => setConflictRows(null)}
        />
      )}
    </>
  );
}

function nextPosition(rewards: Reward[]): number {
  if (rewards.length === 0) return 1;
  return Math.max(...rewards.map((r) => r.position)) + 1;
}

// ──────────────────────────────────────────────────────────────────────────────
// RewardsEmptyState — mockup L2238–2248
// ──────────────────────────────────────────────────────────────────────────────

function RewardsEmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon} aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="20 12 20 22 4 22 4 12" />
          <rect x="2" y="7" width="20" height="5" />
          <line x1="12" y1="22" x2="12" y2="7" />
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
      </span>
      <h3>No rewards configured</h3>
      <p>Add rewards to materialize prizes when each cycle closes.</p>
      <Button
        variant="primary"
        onClick={onAdd}
        icon={<Plus size={12} aria-hidden="true" />}
      >
        Add reward
      </Button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Per-row kebab menu
// ──────────────────────────────────────────────────────────────────────────────

function RewardRow({
  reward,
  tiers,
  showTier,
  edited,
  onEdit,
  onDelete,
}: {
  reward: Reward;
  tiers: Tier[];
  showTier: boolean;
  /** The row has moved away from what the server holds and is waiting for one
     of the two saves in the bar below. */
  edited?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const kind = rewardKind(reward);
  const orphan = showTier && isOrphanTierKey(reward, tiers);
  return (
    <tr className={orphan ? styles.rowOrphan : undefined}>
      <td className={styles.num}>
        <span className={styles.posCell}>
          {/* TODO(reward-drag): wire actual drag-reorder once the order
              semantics are confirmed; manual position edit is sufficient for now */}
          <span className={styles.drag} aria-hidden="true" title="Drag to reorder">
            ≡
          </span>
          {reward.position}
          {edited && (
            /* role="img" rather than a bare span: `aria-label` names an
               element only where its role allows a name from the author, and
               a span with no role is `generic`, which does not. The dot is a
               graphic standing for a sentence, so `img` is what it is. */
            <span
              className={styles.dirtyDot}
              role="img"
              title="Edited — not saved yet"
              aria-label="Edited — not saved yet"
            />
          )}
        </span>
      </td>
      <td>
        <b>{reward.name}</b>
      </td>
      {showTier && (
        <td>
          <TierChip reward={reward} tiers={tiers} />
        </td>
      )}
      <td>
        <span className={`${styles.badge} ${badgeClassFor(kind)}`}>{kind}</span>
      </td>
      <td
        className={`${styles.monoCell} ${
          kind === "tier_threshold" ? styles.rangeNone : ""
        }`}
      >
        {rewardRangeText(reward)}
      </td>
      <td>
        <span className={styles.payloadCell} title={rewardPayloadText(reward.payload)}>
          {rewardPayloadText(reward.payload)}
        </span>
      </td>
      <td className={styles.act}>
        <RowMenu onEdit={onEdit} onDelete={onDelete} />
      </td>
    </tr>
  );
}

/**
 * Tier cell of a reward row. Three shapes, per the t-24 mockup:
 *   - bound to a configured tier → neutral chip with the tier's name
 *   - no tier_key → dashed "All tiers" chip; the reward pays out in each tier
 *   - tier_key missing from tier_config → red chip with the raw key (we have
 *     no name for it) and a warning glyph
 */
function TierChip({ reward, tiers }: { reward: Reward; tiers: Tier[] }) {
  if (!reward.tier_key) {
    return (
      <span
        className={`${styles.tierChip} ${styles.tierChipAll}`}
        title="No tier_key — this reward is granted inside every tier"
      >
        All tiers
      </span>
    );
  }
  const tier = tiers.find((t) => t.key === reward.tier_key);
  if (tier) {
    return (
      <span className={styles.tierChip} title={`tier_key: ${tier.key}`}>
        {tierLabel(tier)}
      </span>
    );
  }
  return (
    <span
      className={`${styles.tierChip} ${styles.tierChipOrphan}`}
      title={`tier_key "${reward.tier_key}" is not in this board's tier config — the reward is never granted`}
    >
      <AlertTriangle size={11} strokeWidth={2.5} aria-hidden="true" />
      {reward.tier_key}
    </span>
  );
}

/**
 * Banner above the table when at least one reward points at a tier the config
 * no longer has. tier_key can't be patched (RewardUpdateParams has no such
 * field), so the only remedy is delete + recreate.
 */
function OrphanRewardsAlert({ count }: { count: number }) {
  const plural = count > 1;
  return (
    <Alert
      variant="danger"
      title={`${count} reward${plural ? "s" : ""} point${plural ? "" : "s"} at a tier that no longer exists`}
    >
      {plural ? "Their " : "Its "}
      <code>tier_key</code> is not in this board&apos;s tier config, so{" "}
      {plural ? "they" : "it"} will never be granted. <code>tier_key</code>{" "}
      can&apos;t be edited after creation — delete the reward and add it again
      with a valid tier.
    </Alert>
  );
}

function RowMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Menu
      aria-label="Reward actions"
      placement="bottom-end"
      items={[
        { label: "Edit", onSelect: onEdit },
        { label: "Delete", variant: "danger", onSelect: onDelete },
      ]}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Inline editor row (used for both add and edit). Fields mirror mockup
// L2366–2380 (add) and L2538–2552 (edit). Range pair input is duplicated
// (from/to). For percentile, both are 0–100 and rendered with a "%" suffix.
// ──────────────────────────────────────────────────────────────────────────────

interface RewardFormState {
  position: number;
  name: string;
  kind: RewardKind;
  /** ALL_TIERS ("") means: don't send tier_key at all. */
  tierKey: string;
  rangeFrom: string;
  rangeTo: string;
  payload: string;
}

function rewardToFormState(r: Reward): RewardFormState {
  const k = rewardKind(r);
  return {
    position: r.position,
    name: r.name,
    kind: k,
    tierKey: r.tier_key ?? ALL_TIERS,
    rangeFrom:
      k === "percentile"
        ? String(r.percentile_from ?? "")
        : String(r.rank_from ?? ""),
    rangeTo:
      k === "percentile"
        ? String(r.percentile_to ?? "")
        : String(r.rank_to ?? ""),
    payload: rewardPayloadToInput(r.payload),
  };
}

function RewardEditorRow(
  props:
    | {
        mode: "add";
        lbId: string;
        tiers: Tier[];
        showTier: boolean;
        gated: boolean;
        /** Which of the two tables this drawer belongs to. */
        basis: RankBasis;
        initial: RewardFormState;
        /** Where the finished row goes. The drawer writes nothing to the
           server: the table is saved as a whole, from the bar below it. */
        onCommit: (row: Reward) => void;
        onClose: () => void;
      }
    | {
        mode: "edit";
        lbId: string;
        tiers: Tier[];
        showTier: boolean;
        gated: boolean;
        basis: RankBasis;
        reward: Reward;
        initial: RewardFormState;
        onCommit: (row: Reward) => void;
        onClose: () => void;
      },
) {
  const [form, setForm] = useState<RewardFormState>(props.initial);
  const [error, setError] = useState<string | null>(null);

  function onSave() {
    setError(null);
    const name = form.name.trim();
    if (!name) {
      setError("Name is required.");
      return;
    }
    const pos = Number(form.position);
    if (!Number.isFinite(pos) || !Number.isInteger(pos) || pos < 1) {
      setError("Position must be a positive integer.");
      return;
    }
    const parsed = parseRewardPayload(form.payload);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    const payload = parsed.value;

    if (props.mode === "edit") {
      // Range fields are immutable per UpdateRewardInput contract, so an edit
      // only ever moves these three.
      props.onCommit({ ...props.reward, name, position: pos, payload });
      return;
    }

    const row: Reward = {
      id: draftRewardId(),
      leaderboard_id: props.lbId,
      rank_basis: props.basis,
      name,
      position: pos,
      payload,
      created_at: "",
    };

    if (form.kind === "tier_threshold") {
      // The tier IS the scope of a threshold reward, so "All tiers" would
      // leave it pointing at nothing — ValidateRewardCreate rejects it.
      if (form.tierKey === ALL_TIERS) {
        setError('tier_threshold needs a specific tier — "All tiers" is not allowed.');
        return;
      }
      // No range at all: that absence is what makes it a ladder rung.
      props.onCommit({ ...row, tier_key: form.tierKey });
      return;
    }

    const from = Number(form.rangeFrom);
    const to = Number(form.rangeTo);
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      setError("Range bounds must be numbers.");
      return;
    }
    if (form.kind === "percentile" && (from < 0 || to > 100)) {
      setError("Percentile bounds must be within 0–100.");
      return;
    }
    if (from > to) {
      setError("Range start must be ≤ end.");
      return;
    }

    // Left unset for "All tiers": a reward with no tier_key is the one that
    // matches inside every tier, and `createInputOf` keeps the key off the
    // wire for exactly that reason.
    if (props.showTier && form.tierKey !== ALL_TIERS) {
      row.tier_key = form.tierKey;
    }
    if (form.kind === "rank_range") {
      row.rank_from = from;
      row.rank_to = to;
    } else {
      row.percentile_from = from;
      row.percentile_to = to;
    }
    props.onCommit(row);
  }

  const percentSuffix = form.kind === "percentile" ? "%" : "";

  /** The second table of a member_contribution board — rank ranges only. */
  const contributorTable = props.basis === "guild_member";

  // The starting league — tier_config[0] — is never offered as a threshold
  // reward's tier: the claimed ceiling starts there, so a reward bound to it
  // can never be granted and ValidateRewardCreate rejects the pair. The other
  // two kinds keep it: they are materialized on cycle close, where the bottom
  // tier is an ordinary partition. Only while the selector can actually be
  // used — in edit mode it is frozen, and a reward already bound to the bottom
  // tier (created before the API closed this) still has to show what it says.
  const selectableTiers =
    props.mode === "add" && form.kind === "tier_threshold"
      ? props.tiers.slice(1)
      : props.tiers;

  // "All tiers" first (muted, then a hairline), the configured tiers after it.
  // In edit mode the value is frozen but still has to render, so a tier_key
  // that has since left the config is appended as its own option.
  const tierOptions = [
    {
      value: ALL_TIERS,
      label: "All tiers",
      muted: true,
      separatorAfter: selectableTiers.length > 0,
    },
    ...selectableTiers.map((t) => ({
      value: t.key,
      label: tierLabel(t),
      meta: t.key,
    })),
    ...(form.tierKey !== ALL_TIERS &&
    !props.tiers.some((t) => t.key === form.tierKey)
      ? [{ value: form.tierKey, label: form.tierKey, mono: true }]
      : []),
  ];

  return (
    <div
      className={`${styles.editRow} ${props.showTier ? styles.editRowTier : ""} ${
        props.gated ? styles.editRowGated : ""
      }`}
    >
      <input
        type="number"
        inputMode="numeric"
        className={`${styles.input} ${styles.inputMono}`}
        value={form.position}
        onChange={(e) =>
          setForm((f) => ({ ...f, position: Number(e.target.value) }))
        }
        placeholder="Pos."
        title="position"
        aria-label="Position"
      />
      <input
        type="text"
        className={styles.input}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder="Name"
        aria-label="Name"
      />
      {props.showTier && (
        <Select
          aria-label="Tier"
          className={styles.tierSelect}
          value={form.tierKey}
          options={tierOptions}
          /* tier_key is create-only — RewardUpdateParams can't change it. */
          disabled={props.mode === "edit"}
          onChange={(v) => setForm((f) => ({ ...f, tierKey: v }))}
        />
      )}
      <div
        role="radiogroup"
        aria-label="Type"
        className={styles.seg}
      >
        <button
          type="button"
          role="radio"
          aria-checked={form.kind === "rank_range"}
          className={`${styles.segOpt} ${form.kind === "rank_range" ? styles.segOptActive : ""}`}
          disabled={props.mode === "edit"}
          onClick={() => setForm((f) => ({ ...f, kind: "rank_range" }))}
        >
          rank_range
        </button>
        {/* A contributor reward is a rank range and nothing else: the server
            accepts `rank_basis=guild_member` only with rank_from/rank_to, and
            neither a percentile band nor a tier threshold has any meaning
            inside one team. So the second table's editor offers the one type
            it can save rather than two that answer 400. */}
        {!contributorTable && (
          <button
            type="button"
            role="radio"
            aria-checked={form.kind === "percentile"}
            className={`${styles.segOpt} ${form.kind === "percentile" ? styles.segOptActive : ""}`}
            disabled={props.mode === "edit"}
            onClick={() => setForm((f) => ({ ...f, kind: "percentile" }))}
          >
            percentile
          </button>
        )}
        {/* Only a claim_gated board accepts one — ValidateRewardCreate turns
            it into a 400 anywhere else, so the option isn't offered there. */}
        {props.gated && !contributorTable && (
          <button
            type="button"
            role="radio"
            aria-checked={form.kind === "tier_threshold"}
            className={`${styles.segOpt} ${form.kind === "tier_threshold" ? styles.segOptActive : ""}`}
            disabled={props.mode === "edit"}
            onClick={() =>
              setForm((f) => ({
                ...f,
                kind: "tier_threshold",
                // The previous kind may have left it on the starting league,
                // which is a legal scope there and not here. Back to "All
                // tiers", where Save asks for a real one, rather than keeping
                // a value the picker no longer offers.
                tierKey:
                  f.tierKey === props.tiers[0]?.key ? ALL_TIERS : f.tierKey,
              }))
            }
          >
            tier_threshold
          </button>
        )}
      </div>
      {form.kind === "tier_threshold" ? (
        // No range to collect — the Tier column above is the whole scope.
        <div className={styles.rangeNA}>— tier threshold</div>
      ) : (
        <div className={styles.rangePair}>
          <input
            type="number"
            inputMode="numeric"
            className={`${styles.input} ${styles.inputMono} ${styles.inputRange}`}
            value={form.rangeFrom}
            onChange={(e) =>
              setForm((f) => ({ ...f, rangeFrom: e.target.value }))
            }
            disabled={props.mode === "edit"}
            aria-label="Range from"
          />
          {percentSuffix && (
            <span className={styles.percentSuffix} aria-hidden="true">
              %
            </span>
          )}
          <span className={styles.dash} aria-hidden="true">
            –
          </span>
          <input
            type="number"
            inputMode="numeric"
            className={`${styles.input} ${styles.inputMono} ${styles.inputRange}`}
            value={form.rangeTo}
            onChange={(e) => setForm((f) => ({ ...f, rangeTo: e.target.value }))}
            disabled={props.mode === "edit"}
            aria-label="Range to"
          />
          {percentSuffix && (
            <span className={styles.percentSuffix} aria-hidden="true">
              %
            </span>
          )}
        </div>
      )}
      <textarea
        className={`${styles.input} ${styles.inputMono} ${styles.editPayload}`}
        value={form.payload}
        onChange={(e) => setForm((f) => ({ ...f, payload: e.target.value }))}
        placeholder='{"gold":250,"gems":10}'
        aria-label="Payload"
      />
      <div className={styles.editActions}>
        <Button variant="ghost" size="sm" onClick={props.onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={onSave}>
          Save
        </Button>
      </div>
      {error && (
        <div className={styles.editError} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reward preview sidebar — mockup L2083–2138 / L2421–2429 / L2585–2593.
// ──────────────────────────────────────────────────────────────────────────────

/** One tier's slice of the preview: its own top, ranked from 1. */
interface TierGroup {
  tier: Tier;
  entries: Entry[];
}

function RewardPreviewSidebar({
  editorState,
  rewards,
  topEntries,
  tierGroups,
  cycle,
  topLoading,
  topError,
}: {
  editorState:
    | { kind: "add"; basis: RankBasis }
    | { kind: "edit"; reward: Reward }
    | null;
  rewards: Reward[];
  topEntries: Entry[];
  /** Non-null on a tier board — then the preview is grouped, not flat. */
  tierGroups: TierGroup[] | null;
  cycle: Cycle | undefined;
  topLoading: boolean;
  topError: boolean;
}) {
  let sub: React.ReactNode;
  if (editorState?.kind === "add") {
    sub = "Live preview — updates when you save";
  } else if (editorState?.kind === "edit") {
    sub = "Live preview — updates when you save";
  } else if (rewards.length === 0) {
    sub = "No rewards configured — preview empty.";
  } else if (cycle) {
    sub = (
      <>
        Based on current cycle top — <code>{shortCycleId(cycle.id)}</code>
        {tierGroups !== null && ` · top ${TIER_PREVIEW_LIMIT} per tier`}
      </>
    );
  } else {
    sub = "No active cycle to preview against.";
  }

  // Body content
  let body: React.ReactNode = null;
  if (editorState?.kind === "add") {
    body = (
      <div className={styles.previewMessage}>
        Add the new reward to see its effect on the current top.
      </div>
    );
  } else if (editorState?.kind === "edit") {
    body = (
      <div className={styles.previewMessage}>
        Editing <b>{editorState.reward.name}</b> — preview will refresh on save.
      </div>
    );
  } else if (rewards.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        Add a reward to see who would receive it.
      </div>
    );
  } else if (topError) {
    body = (
      <div className={styles.previewMessage}>
        Couldn&apos;t load top entries for preview.
      </div>
    );
  } else if (topLoading) {
    body = (
      <div className={styles.previewMessage}>
        <Skeleton height={120} />
      </div>
    );
  } else if (tierGroups !== null && tierGroups.length === 0) {
    // A tier board whose tier_config is empty — the API refuses to create one,
    // so this only shows up on legacy rows. Say so instead of drawing nothing.
    body = (
      <div className={styles.previewMessage}>
        No tiers configured — nothing to preview.
      </div>
    );
  } else if (tierGroups !== null) {
    // Grouped preview: each tier is ranked on its own (same partitioning the
    // grant SQL uses), and only the rewards that reach into that tier — its
    // own plus the unbound ones — are matched against its entries. A reward
    // whose tier_key left the config belongs to no group and shows nowhere.
    body = tierGroups.map((g) => (
      <Fragment key={g.tier.key}>
        <div className={styles.prevGroupHead}>
          <span className={styles.prevGroupName}>{tierLabel(g.tier)}</span>
          <span className={styles.prevGroupKey}>{g.tier.key}</span>
          <span className={styles.prevGroupCount}>{g.entries.length}</span>
        </div>
        {g.entries.length === 0 ? (
          <div className={styles.prevGroupEmpty}>No entries in this tier</div>
        ) : (
          <PreviewRows
            entries={g.entries}
            rewards={rewardsForTier(rewards, g.tier.key)}
          />
        )}
      </Fragment>
    ));
  } else if (topEntries.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        No entries yet — preview will populate as players submit scores.
      </div>
    );
  } else {
    body = <PreviewRows entries={topEntries} rewards={rewards} />;
  }

  return (
    <aside className={styles.preview}>
      <div className={styles.previewHead}>
        <h3>Reward preview</h3>
        <div className={styles.previewSub}>{sub}</div>
      </div>
      {body}
    </aside>
  );
}

/**
 * The note under the two tables: what «contributors only» actually covers, and
 * which of the two halves of it is a setting at all.
 *
 * It is a card of its own rather than a line under either table, because it is
 * about both — and it names the toggle's CURRENT position, so the sentence
 * describes the board in front of the operator instead of the feature in
 * general.
 */
function ContributorsOnlyNote({ on }: { on: boolean }) {
  return (
    <div className={styles.card}>
      <div className={styles.noteBody}>
        <span className={styles.infoGlyph} aria-hidden="true">
          i
        </span>
        <div className={styles.noteText}>
          Both tables pay out to contributors only. For the team table that is
          the <code>reward_contributors_only</code> toggle on the Settings tab,
          currently {on ? "on" : "off"} — turn it {on ? "off" : "on"} to{" "}
          {on
            ? "restore the old fan-out to every current member of a winning team"
            : "hand the placement grant to contributors alone"}
          . For the contributor table a contribution above zero is
          unconditional: a prize place nobody qualifies for is simply not
          awarded.
        </div>
      </div>
    </div>
  );
}

/**
 * The room the teams race in — the same live-top preview every other board
 * gets, worded for a board whose rows are teams and matched against the TEAM
 * table alone.
 */
function RoomPreviewSidebar({
  editorState,
  rewards,
  roomEntries,
  roomSize,
  bucketId,
  hasRooms,
  cycle,
  roomLoading,
  roomError,
}: {
  /** The TEAM table's editor alone — the other table cannot move these rows. */
  editorState:
    | { kind: "add"; basis: RankBasis }
    | { kind: "edit"; reward: Reward }
    | null;
  rewards: Reward[];
  /** The page of ONE room — the same rows the ranks below belong to. */
  roomEntries: Entry[];
  /** How many teams that room holds — the percentile denominator, which is
     not the page length when the page stopped short of the room. */
  roomSize: number;
  /** Which room that is; absent on a board with `segmentation=none`, and also
     for as long as the pair of reads is still in flight. */
  bucketId: string | undefined;
  /** Whether the board has rooms at all. Read off the board rather than off
     `bucketId`, which is unknown until the reads land — a title that changed
     when they did would rename the panel under the operator. */
  hasRooms: boolean;
  cycle: Cycle | undefined;
  roomLoading: boolean;
  roomError: boolean;
}) {
  let sub: ReactNode;
  if (editorState) {
    sub = "Live preview — updates when you save";
  } else if (rewards.length === 0) {
    sub = "No team rewards configured — preview empty.";
  } else if (cycle) {
    // The room NAMED here is the room the rows below were fetched from — one
    // value from one query, so the caption can never describe a different
    // ranking from the one on screen.
    sub = bucketId ? (
      <>
        Room <code>{shortRoomId(bucketId)}</code> of the open window —{" "}
        <code>{shortCycleId(cycle.id)}</code>
      </>
    ) : (
      <>
        The open window — <code>{shortCycleId(cycle.id)}</code>
      </>
    );
  } else {
    sub = "No active cycle to preview against.";
  }

  let body: ReactNode;
  if (editorState) {
    body = (
      <div className={styles.previewMessage}>
        {editorState.kind === "add" ? (
          "Add the new reward to see its effect on the current top."
        ) : (
          <>
            Editing <b>{editorState.reward.name}</b> — preview will refresh on
            save.
          </>
        )}
      </div>
    );
  } else if (rewards.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        Add a team reward to see which teams would receive it.
      </div>
    );
  } else if (roomError) {
    body = (
      <div className={styles.previewMessage}>
        Couldn&apos;t load the room for preview.
      </div>
    );
  } else if (roomLoading) {
    body = (
      <div className={styles.previewMessage}>
        <Skeleton height={120} />
      </div>
    );
  } else if (roomEntries.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        No team has entered yet — preview will populate as they do.
      </div>
    );
  } else {
    body = (
      <PreviewRows
        entries={roomEntries}
        rewards={rewards}
        total={roomSize}
        chipClass={contributionChipClass}
        emptyBadge
      />
    );
  }

  return (
    <aside className={styles.preview}>
      <div className={styles.previewHead}>
        {/* A board with `segmentation=none` has no room to preview, and the
           caption under this already says so — the title has to agree with it
           rather than promise a partition the board does not have. */}
        <h3>{hasRooms ? "Room preview" : "Cycle preview"}</h3>
        <div className={styles.previewSub}>{sub}</div>
      </div>
      {body}
    </aside>
  );
}

/**
 * The inside of one team: its members ranked by contribution, each carrying
 * both grants a closing window would hand them — their own place in the team
 * (the contributor table) and the team's place in the room (the team table).
 *
 * The team-table chip is deliberately repeated on every qualifying row: that
 * is what the grant does. Who qualifies is `reward_contributors_only` — with
 * it on, the members with a contribution; with it off, every current member,
 * a zero included.
 */
function TeamPreviewSidebar({
  editorState,
  memberRewards,
  entityRewards,
  roomSize,
  teamEntry,
  contributions,
  rewardContributorsOnly,
  loading,
  error,
}: {
  /** The CONTRIBUTOR table's editor alone, by the same split the cards use. */
  editorState:
    | { kind: "add"; basis: RankBasis }
    | { kind: "edit"; reward: Reward }
    | null;
  memberRewards: Reward[];
  entityRewards: Reward[];
  /** How many teams the room holds — the same denominator the panel above
     divides by, so the two can never disagree about what this team wins. */
  roomSize: number;
  /** The team's own row in the room — what its placement grant is read from. */
  teamEntry: Entry | undefined;
  contributions: GuildContributions | undefined;
  rewardContributorsOnly: boolean;
  loading: boolean;
  error: boolean;
}) {
  // Both read off the contributions answer, which is the authority on the team
  // this panel is about: it was asked for by id, and it carries the team's own
  // seat in the room. The room row is only the fallback for the moment before
  // that answer lands.
  const team = contributions?.guild;
  const name = team?.guild.name ?? teamEntry?.player.display_name;
  const rank = team?.entered ? team.rank : teamEntry?.rank;

  let sub: ReactNode;
  if (editorState) {
    sub = "Live preview — updates when you save";
  } else if (!teamEntry) {
    sub = "No team has entered this cycle yet.";
  } else if (name && rank !== undefined) {
    sub = (
      <>
        Contributions inside <code>{name}</code> — {ordinal(rank)} in its room
      </>
    );
  } else if (name) {
    sub = (
      <>
        Contributions inside <code>{name}</code>
      </>
    );
  } else {
    sub = "Contributions inside the leading team";
  }

  // The team's own placement chips. Both halves come from the ROOM: `rank` is
  // the team's seat in it (the contributions answer, which is what the grants
  // are computed from), and the denominator is how many teams that room holds
  // — the same number `PreviewRows` divides by for this very team's row in the
  // panel above.
  const teamChips =
    rank === undefined
      ? []
      : entityRewards.filter((r) => rewardMatchesRank(r, rank, roomSize));

  let body: ReactNode;
  if (editorState) {
    body = (
      <div className={styles.previewMessage}>
        {editorState.kind === "add" ? (
          "Add the new reward to see who inside the team would collect it."
        ) : (
          <>
            Editing <b>{editorState.reward.name}</b> — preview will refresh on
            save.
          </>
        )}
      </div>
    );
  } else if (!teamEntry) {
    body = (
      <div className={styles.previewMessage}>
        A team takes a room seat on its first counted submit — the preview
        follows the first that does.
      </div>
    );
  } else if (error) {
    body = (
      <div className={styles.previewMessage}>
        Couldn&apos;t load the team&apos;s contributions.
      </div>
    );
  } else if (loading || !contributions) {
    body = (
      <div className={styles.previewMessage}>
        <Skeleton height={120} />
      </div>
    );
  } else if (contributions.members.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        This team has no members to rank.
      </div>
    );
  } else {
    body = (
      <>
        {contributions.members.map((m) => {
          const note = noRankNote(m);
          const chips = [
            ...memberRewards.filter((r) => rewardCoversRank(r, m.rank)),
            // The placement grant reaches this member when the board says it
            // does — contributors only, or everyone in the team.
            ...(!rewardContributorsOnly || m.rank !== undefined
              ? teamChips
              : []),
          ];
          return (
            <div key={m.player.id} className={styles.prevRow}>
              <span className={styles.rk}>{m.rank ?? "—"}</span>
              <span
                className={`${styles.nm} ${note ? styles.nmMuted : ""}`}
              >
                {m.player.display_name}
                {note}
              </span>
              <div className={styles.chips}>
                {chips.length === 0 ? (
                  <span className={styles.chipNone}>—</span>
                ) : (
                  chips.map((r) => (
                    <span
                      key={r.id}
                      className={`${styles.rewardChip} ${contributionChipClass(r)}`}
                    >
                      {r.name}
                    </span>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  return (
    <aside className={styles.preview}>
      <div className={styles.previewHead}>
        <h3>Team preview</h3>
        <div className={styles.previewSub}>{sub}</div>
      </div>
      {body}
    </aside>
  );
}

/** «6th», «1st» — the place a team took, as the subtitle reads it aloud. */
function ordinal(n: number): string {
  const rest100 = n % 100;
  if (rest100 >= 11 && rest100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** One entry per row with the reward chips it would collect. */
function PreviewRows({
  entries,
  rewards,
  total,
  chipClass = chipClassFor,
  emptyBadge = false,
}: {
  entries: Entry[];
  rewards: Reward[];
  /**
   * How many entries the ranking holds, for the percentile arithmetic.
   * Defaults to the loaded rows, which is what every board but the two-table
   * one has always divided by — there the page IS the whole list it previews.
   */
  total?: number;
  /** How the chips are tinted. Defaults to the rule every board but the
     two-table one uses — see `contributionChipClass`. */
  chipClass?: (r: Reward) => string;
  /**
   * Whether a row that wins nothing says so with a `—` instead of leaving the
   * cell blank. Off by default, which is byte for byte what every board but
   * the two-table one has always rendered; the t-149 mockup draws the badge,
   * and it is what makes the edge of the prize places readable at a glance.
   */
  emptyBadge?: boolean;
}) {
  const denominator = total ?? entries.length;
  return (
    <>
      {entries.map((e) => {
        const matched = rewards.filter((r) =>
          rewardMatchesEntry(r, e, denominator),
        );
        return (
          <div key={e.player.id} className={styles.prevRow}>
            <span className={styles.rk}>{e.rank}</span>
            <span className={styles.nm}>{e.player.display_name}</span>
            <div className={styles.chips}>
              {emptyBadge && matched.length === 0 && (
                <span className={styles.chipNone}>—</span>
              )}
              {matched.map((r) => (
                <span
                  key={r.id}
                  className={`${styles.rewardChip} ${chipClass(r)}`}
                >
                  {r.name}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Delete confirm modal — mockup L2752–2769.
// ──────────────────────────────────────────────────────────────────────────────

function DeleteRewardModal({
  reward,
  onConfirm,
  onClose,
}: {
  reward: Reward;
  /** Takes the row out of the draft. Nothing is written until the table is
     saved, which is what the bar below the table is for. */
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete reward ${reward.name}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Delete
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        This will remove the reward template. Already-materialized grants in
        past cycles are not affected.
      </p>
    </Modal>
  );
}
